import { lazy, Suspense, useCallback, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { scrollToId } from "../lib/site";

const HeroScene = lazy(() => import("./HeroScene"));
const EASE = [0.2, 0.8, 0.2, 1];
const LINES = [
  [["Sua marca"]],
  [["merece"]],
  [["um site"]],
  [["à altura.", true]],
];

const Fallback = ({ hidden }) => (
  <div className={`absolute inset-0 grid place-items-center transition-opacity duration-1000 ${hidden ? "opacity-0" : "opacity-100"}`} aria-hidden="true">
    <div className="absolute w-[70%] aspect-square rounded-full bg-[radial-gradient(circle,rgba(212,175,90,0.22),transparent_65%)] blur-2xl" />
    <div className="fallback-sculpture w-[38%] aspect-square rotate-45 rounded-[18%] bg-[linear-gradient(135deg,#1c1a16,#050505)] shadow-[inset_1px_1px_0_rgba(212,175,90,.5),0_40px_80px_-20px_rgba(0,0,0,.9)]" />
  </div>
);

export const Hero = ({ onBrief }) => {
  const ref = useRef(null);
  const anchor = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const onReady = useCallback(() => setReady(true), []);
  const onFail = useCallback(() => setFailed(true), []);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yScene = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden">
      <div className="pointer-events-none absolute -top-40 right-[-10%] w-[70vw] h-[70vw] max-w-[900px] max-h-[900px] rounded-full bg-[radial-gradient(circle,rgba(212,175,90,0.10),transparent_60%)]" />
      {!failed && (
        <motion.div style={{ y: yScene }} initial={{ opacity: 0 }} animate={{ opacity: ready ? 1 : 0 }} transition={{ duration: 2, ease: EASE }} className="pointer-events-none absolute inset-0 z-0">
          <Suspense fallback={null}>
            <HeroScene onReady={onReady} onFail={onFail} anchorRef={anchor} />
          </Suspense>
        </motion.div>
      )}
      <div className="hero-sweep pointer-events-none absolute inset-0 z-[1]" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(180deg,rgba(5,5,5,.55),transparent_28%,transparent_72%,rgba(5,5,5,.85))] lg:bg-[linear-gradient(90deg,rgba(5,5,5,.9)_0%,rgba(5,5,5,.55)_38%,transparent_62%)]" />
      <div className="relative z-[2] mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 pt-24 lg:pt-0 grid lg:grid-cols-12 lg:min-h-[100svh] [grid-template-areas:'a''s''b'] lg:[grid-template-areas:'a_s''b_s'] lg:grid-rows-[1fr_auto]">
        <motion.div style={{ y: yText }} className="[grid-area:a] lg:col-span-7 lg:self-end lg:pt-32 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.4 }} className="label flex items-center gap-3 mb-6 lg:mb-8">
            <span className="h-px w-8 bg-[#D4AF5A]" /> Estúdio digital independente
          </motion.p>
          <h1 data-testid="hero-title" className="font-display font-extrabold uppercase text-[1.95rem] min-[400px]:text-[2.2rem] leading-[0.98] sm:text-6xl lg:text-[3.8rem] xl:text-[4.5rem] lg:leading-[0.95] tracking-[-0.03em] drop-shadow-[0_2px_24px_rgba(0,0,0,.6)]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.08em]">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay: 0.55 + i * 0.13, ease: EASE }}>
                  {line.map(([w, g]) => g
                    ? <span key={w} className="hero-gold">{w}</span>
                    : <span key={w}>{w}</span>)}
                </motion.span>
              </span>
            ))}
          </h1>
        </motion.div>

        <div ref={anchor} className="[grid-area:s] lg:col-span-5 relative h-[340px] sm:h-[460px] lg:h-auto lg:min-h-[100svh] pointer-events-none">
          <Fallback hidden={ready && !failed} />
          <span className="hidden lg:block absolute top-28 right-0 label text-right leading-loose">Estúdio digital<br />brasileiro</span>
        </div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1.05, ease: EASE }} className="[grid-area:b] lg:col-span-7 relative z-10 pb-14 lg:pb-24 lg:pt-10">
          <p className="max-w-[34rem] text-[#A7A7AD] text-[15px] sm:text-lg leading-relaxed">
            Criamos sites institucionais e landing pages que combinam identidade visual, experiência de usuário e estratégia para transformar a presença digital da sua empresa.
          </p>
          <div className="mt-8 lg:mt-10 flex flex-col sm:flex-row gap-3">
            <button onClick={onBrief} data-testid="hero-primary-cta" className="btn-gold h-14 px-8 rounded-full font-semibold text-[13px] tracking-[0.1em] uppercase inline-flex items-center justify-center gap-2">
              Solicitar um projeto <ArrowUpRight size={16} />
            </button>
            <button onClick={() => scrollToId("projetos")} data-testid="hero-secondary-cta" className="btn-ghost h-14 px-8 rounded-full font-medium text-[13px] tracking-[0.1em] uppercase text-[#F5F3EE]">
              Explorar projetos
            </button>
          </div>
        </motion.div>
      </div>
      <button onClick={() => scrollToId("servicos")} data-testid="hero-scroll" className="hidden lg:flex absolute bottom-8 right-12 label items-center gap-2 hover:text-[#F5F3EE] transition-colors">
        Scroll <ArrowDown size={14} className="animate-bounce" />
      </button>
    </section>
  );
};
